import { isValidPhone } from '../shared/contact-validation';

declare global {
    interface Window {
        turnstile?: { reset(container: string): void };
    }
}

export function initContactForm() {
    const form = document.querySelector<HTMLFormElement>('#contact-form');
    const fields = form?.querySelector('fieldset');
    const label = form?.querySelector<HTMLElement>('[data-contact-submit-label]');
    const success = form?.querySelector<HTMLElement>('[data-contact-success]');
    const error = form?.querySelector<HTMLElement>('[data-contact-error]');
    const phone = form?.querySelector<HTMLInputElement>('#contact-phone');
    const phoneError = form?.querySelector<HTMLElement>('#contact-phone-error');
    if (!form || !fields || !label || !success || !error || !phone || !phoneError) return;

    const phoneMessage = 'הזינו מספר טלפון עם 7 עד 15 ספרות. אפשר להוסיף קידומת, רווחים ומקפים.';
    const showPhoneError = (message: string) => {
        phoneError.textContent = message;
        phoneError.hidden = !message;
        phone.setAttribute('aria-invalid', String(Boolean(message)));
    };
    phone.addEventListener('input', () => {
        const message = phone.value && !isValidPhone(phone.value) ? phoneMessage : '';
        phone.setCustomValidity(message);
        if (!phoneError.hidden) showPhoneError(message);
    });
    phone.addEventListener('blur', () => {
        if (phone.value && !isValidPhone(phone.value)) showPhoneError(phoneMessage);
    });
    form.addEventListener(
        'invalid',
        (event) => {
            const input = event.target;
            if (!(input instanceof HTMLInputElement)) return;
            if (input === phone) {
                const message = phone.value ? phoneMessage : 'הזינו טלפון לחזרה.';
                phone.setCustomValidity(message);
                showPhoneError(message);
            }
        },
        true
    );

    let status: 'idle' | 'sending' | 'success' | 'error' = 'idle';
    const submitLabel = label.textContent;
    const errorLabel = error.querySelector('strong');
    const errorText = errorLabel?.textContent;

    const setStatus = (next: typeof status) => {
        status = next;
        form.setAttribute('aria-busy', String(next === 'sending'));
        fields.disabled = next === 'sending' || next === 'success';
        label.textContent = next === 'sending' ? 'שולחים את הפנייה…' : submitLabel;
        success.hidden = next !== 'success';
        error.hidden = next !== 'error';
        if (errorLabel) errorLabel.textContent = errorText ?? '';
    };

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (status === 'sending' || status === 'success') return;

        // Read the fields before disabling them; disabled fields are omitted from FormData.
        const data = new FormData(form);
        if (!data.get('cf-turnstile-response')) {
            setStatus('error');
            if (errorLabel) errorLabel.textContent = 'השלימו את בדיקת האבטחה ונסו שוב.';
            return;
        }
        setStatus('sending');
        let sent = false;
        try {
            const response = await fetch(form.action, {
                method: 'POST',
                headers: { Accept: 'application/json' },
                body: data,
                signal: AbortSignal.timeout(20_000),
            });
            if (response.status === 429) {
                setStatus('error');
                if (errorLabel) errorLabel.textContent = 'התקבלו פניות רבות. המתינו דקה ונסו שוב.';
                form.querySelector<HTMLButtonElement>('button[type="submit"]')?.focus();
                return;
            }
            if (response.status === 403) {
                setStatus('error');
                if (errorLabel)
                    errorLabel.textContent = 'בדיקת האבטחה לא אושרה. השלימו אותה ונסו שוב.';
                return;
            }
            const result: unknown = await response.json();
            if (
                response.status === 400 &&
                typeof result === 'object' &&
                result !== null &&
                'field' in result &&
                typeof result.field === 'string'
            ) {
                const input = form.elements.namedItem(result.field);
                if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
                    setStatus('idle');
                    const message =
                        input === phone
                            ? phoneMessage
                            : input.type === 'email'
                              ? 'הזינו כתובת אימייל מלאה, למשל name@example.com.'
                              : 'בדקו את הפרטים בשדה הזה ונסו שוב.';
                    input.setCustomValidity(message);
                    if (input === phone) showPhoneError(message);
                    input.addEventListener('input', () => input.setCustomValidity(''), {
                        once: true,
                    });
                    input.reportValidity();
                    input.focus();
                    return;
                }
            }
            if (
                !response.ok ||
                typeof result !== 'object' ||
                result === null ||
                !('sent' in result) ||
                result.sent !== true
            ) {
                throw new Error('Submission not confirmed');
            }
            sent = true;
            setStatus('success');
        } catch {
            setStatus('error');
            form.querySelector<HTMLButtonElement>('button[type="submit"]')?.focus();
        } finally {
            // Turnstile tokens can be used only once, including after a failed delivery.
            if (!sent) window.turnstile?.reset('#contact-verification');
        }
    });
}
